import type { Letter, MatchConfig, Tile } from './types.js';
import { makeRng, shuffle } from './rng.js';

/**
 * The standard 100-tile English distribution and its face values.
 *
 * These are the long-published counts and values of the classic English
 * crossword-tile set: factual game data, not anyone's creative expression.
 * They are used here without reference to any trademarked product name.
 */
export const TILE_COUNTS: Readonly<Record<Letter | '_', number>> = {
  A: 9, B: 2, C: 2, D: 4, E: 12, F: 2, G: 3, H: 2, I: 9, J: 1, K: 1, L: 4,
  M: 2, N: 6, O: 8, P: 2, Q: 1, R: 6, S: 4, T: 6, U: 4, V: 2, W: 2, X: 1,
  Y: 2, Z: 1, _: 2,
};

export const TILE_VALUES: Readonly<Record<Letter | '_', number>> = {
  A: 1, B: 3, C: 3, D: 2, E: 1, F: 4, G: 2, H: 4, I: 1, J: 8, K: 5, L: 1,
  M: 3, N: 1, O: 1, P: 3, Q: 10, R: 1, S: 1, T: 1, U: 1, V: 4, W: 4, X: 8,
  Y: 4, Z: 10, _: 0,
};

/** Tiles in one full set. */
export const SET_SIZE = Object.values(TILE_COUNTS).reduce((a, b) => a + b, 0); // 100

export function valueOf(letter: Letter | null): number {
  return letter === null ? 0 : TILE_VALUES[letter];
}

/**
 * How many tiles the bag must hold so that `seatCount` seats each receive
 * `startingTiles` and then exactly `rounds` draws of `drawPerRound`.
 *
 * This is the rule that keeps match length independent of table size: a
 * 20-seat game needs five sets' worth of tiles to deliver the same nine
 * rounds that four seats get from one set.
 */
export function requiredTiles(seatCount: number, config: MatchConfig): number {
  return seatCount * (config.startingTiles + config.rounds * config.drawPerRound);
}

/** Number of whole 100-tile sets needed to cover {@link requiredTiles}. */
export function requiredSets(seatCount: number, config: MatchConfig): number {
  return Math.max(1, Math.ceil(requiredTiles(seatCount, config) / SET_SIZE));
}

/**
 * The largest `rounds` value a given seat count can sustain from `sets` sets.
 * Used by the lobby to warn when a custom configuration has been pushed past
 * what the tile supply allows.
 */
export function roundsAvailable(
  seatCount: number,
  startingTiles: number,
  drawPerRound: number,
  sets: number,
): number {
  const pool = sets * SET_SIZE - seatCount * startingTiles;
  if (pool < 0) return -1;
  return Math.floor(pool / (seatCount * drawPerRound));
}

export interface BagBuild {
  bag: Tile[];
  /** Tiles trimmed so the division across seats is exact. Never dealt. */
  leftover: Tile[];
  sets: number;
}

/**
 * Build and shuffle the bag for a match.
 *
 * Whole sets are minted so letter frequencies stay faithful, the pile is
 * shuffled, and then it is trimmed to exactly {@link requiredTiles}. Trimming
 * after the shuffle means the discarded remainder is a uniform random sample,
 * so the distribution of what remains is unbiased — and the trimmed tiles are
 * precisely the "some letters may be left over" of the tabletop rule.
 */
export function buildBag(seatCount: number, config: MatchConfig): BagBuild {
  const sets = requiredSets(seatCount, config);
  const all: Tile[] = [];
  let n = 0;
  for (let s = 0; s < sets; s++) {
    for (const [face, count] of Object.entries(TILE_COUNTS)) {
      const isBlank = face === '_';
      const letter = isBlank ? null : (face as Letter);
      for (let i = 0; i < count; i++) {
        all.push({
          id: `t${n++}`,
          letter,
          isBlank,
          assigned: null,
          value: TILE_VALUES[face as Letter | '_'],
        });
      }
    }
  }
  shuffle(all, makeRng(config.seed));
  /*
   * Ids are handed out *after* the shuffle. Minted before it, they ran in face
   * order — t0 to t8 were the As — so any tile id that reached another player
   * told them the letter. Numbered by bag position, an id says only when the
   * tile came out of the bag, which the seat order already tells everyone.
   */
  const numbered = all.map((tile, i): Tile => ({ ...tile, id: `t${i}` }));
  const need = Math.min(requiredTiles(seatCount, config), numbered.length);
  return { bag: numbered.slice(0, need), leftover: numbered.slice(need), sets };
}

/** The letter a tile currently presents; null for an unassigned blank. */
export function faceOf(tile: Tile): Letter | null {
  return tile.isBlank ? tile.assigned : tile.letter;
}
